import React from 'react';
import { HiOutlinePlus } from 'react-icons/hi';

interface SelectionAddToChatProps {
  containerRef: React.RefObject<HTMLElement>;
  canAdd: boolean;
  onAdd: (text: string) => void;
  minChars?: number;
  maxChars?: number;
  allowedSelector?: string;
}


const SelectionAddToChat: React.FC<SelectionAddToChatProps> = ({
  containerRef,
  canAdd,
  onAdd,
  minChars = 3,
  maxChars = 4000,
  allowedSelector = '.message-text',
}) => {
  const [visible, setVisible] = React.useState<boolean>(false);
  const [pos, setPos] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [text, setText] = React.useState<string>('');
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);

  const hide = React.useCallback(() => setVisible(false), []);

  const evaluateSelection = React.useCallback(() => {
    try {
      if (!canAdd) return hide();
      const root = containerRef.current as HTMLElement | null;
      if (!root) return hide();
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return hide();

      const selected = selection.toString().trim();
      if (selected.length < minChars || selected.length > maxChars) return hide();

      const range = selection.getRangeAt(0);
      const within = root.contains(range.startContainer) && root.contains(range.endContainer);
      if (!within) return hide();

      const startEl = (range.startContainer as Node).nodeType === Node.ELEMENT_NODE
        ? (range.startContainer as Element)
        : (range.startContainer.parentElement as Element | null);
      const endEl = (range.endContainer as Node).nodeType === Node.ELEMENT_NODE
        ? (range.endContainer as Element)
        : (range.endContainer.parentElement as Element | null);
      const startAllowed = allowedSelector ? !!startEl && !!startEl.closest(allowedSelector) : true;
      const endAllowed = allowedSelector ? !!endEl && !!endEl.closest(allowedSelector) : true;
      if (!startAllowed || !endAllowed) return hide();

      const rects = range.getClientRects();
      const rect = rects.length ? rects[0] : null;
      if (!rect) return hide();
      const gap = 5;
      const estimatedH = 32; // rough height of pill
      const top = Math.max(8, rect.top - gap - estimatedH);
      const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - 180));

      setPos({ x: left, y: top });
      setText(selected);
      setVisible(true);
    } catch {
      hide();
    }
  }, [canAdd, containerRef, hide, maxChars, minChars]);

  React.useEffect(() => {
    const handleMouseUp = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (buttonRef.current && t && buttonRef.current.contains(t)) return;
      evaluateSelection();
    };
    const handleKeyUp = () => evaluateSelection();
    const handleSelectionChange = () => {
      const sel = window.getSelection();
      const root = containerRef.current as HTMLElement | null;
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !root) return hide();
      const r = sel.getRangeAt(0);
      if (!root.contains(r.startContainer) || !root.contains(r.endContainer)) hide();
    };
    const handleGlobalDismiss = (e: Event) => {
      const t = e.target as Node | null;
      if (buttonRef.current && t && buttonRef.current.contains(t)) return;
      hide();
    };

    document.addEventListener('mouseup', handleMouseUp as EventListener);
    document.addEventListener('keyup', handleKeyUp);
    document.addEventListener('selectionchange', handleSelectionChange as EventListener);
    document.addEventListener('mousedown', handleGlobalDismiss as EventListener);
    window.addEventListener('scroll', handleGlobalDismiss as EventListener, true);
    window.addEventListener('resize', handleGlobalDismiss as EventListener);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp as EventListener);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('selectionchange', handleSelectionChange as EventListener);
      document.removeEventListener('mousedown', handleGlobalDismiss as EventListener);
      window.removeEventListener('scroll', handleGlobalDismiss as EventListener, true);
      window.removeEventListener('resize', handleGlobalDismiss as EventListener);
    };
  }, [evaluateSelection, hide]);

  if (!visible || !canAdd || !text) return null;

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={(e) => {
        e.preventDefault();
        if (text) onAdd(text);
        try { const sel = window.getSelection(); sel && sel.removeAllRanges(); } catch {}
        hide();
      }}
      style={{ position: 'fixed', top: pos.y, left: pos.x, zIndex: 60 }}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium bg-blue-600 text-white border border-blue-600 shadow-sm hover:bg-blue-700 dark:bg-blue-600 dark:text-white dark:border-blue-600 dark:hover:bg-blue-700 backdrop-blur-sm transition-colors duration-150"
      title="Add selection to chat"
    >
      <HiOutlinePlus className="w-3.5 h-3.5" />
      Add to chat
    </button>
  );
};

export default SelectionAddToChat;


